import { Link } from 'react-router-dom';
import { Instagram, Facebook, Linkedin, Mail } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { GolfAuthNavLogo } from '@golfauth/ui';

// Pro Capacitor shell hides the golfer/marketing Quick Links — Find a
// Professional has no native audience, and Contact relocates to the admin
// Settings tab. Web behaviour unchanged.
const IS_NATIVE = Capacitor.isNativePlatform();

// Legal documents become effective on 1 July 2026. Until then, the
// Privacy and Terms links carry a small "(draft)" badge in the
// footer. After 2026-07-01 the date check below evaluates false and
// the badge disappears with no code change required.
const LEGAL_EFFECTIVE_DATE = new Date('2026-07-01T00:00:00Z');
const IS_LEGAL_DRAFT_PERIOD = new Date() < LEGAL_EFFECTIVE_DATE;

interface LegalLink {
  label: string;
  to: string;
  /** Whether this link gets the draft badge during the review period.
   *  The Data Retention Policy is also a draft but the spec only
   *  asks for the badge on Privacy and Terms. */
  draftable: boolean;
}

// Privacy Policy + Terms of Service finalised and published — no longer
// draft. Mechanism kept for any future docs that need a review-period
// badge (e.g. Data Retention remains in draft until 1 July 2026 — flip
// draftable: true on it if/when its footer badge is wanted).
const LEGAL_LINKS: LegalLink[] = [
  { label: 'Privacy Policy', to: '/privacy', draftable: false },
  { label: 'Terms of Service', to: '/terms', draftable: false },
  { label: 'Security', to: '/security', draftable: false },
  { label: 'Cookie Policy', to: '/cookies', draftable: false },
  { label: 'Data Retention', to: '/privacy/data-retention', draftable: false },
  { label: 'Delete Account', to: '/delete-account', draftable: false },
];

function DraftBadge() {
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wider rounded"
      style={{
        backgroundColor: 'rgba(217, 119, 6, 0.15)',
        color: '#92400e',
        lineHeight: 1,
      }}
      title="Working draft pending legal review — effective 1 July 2026"
    >
      Draft
    </span>
  );
}

/**
 * Footer — theme-aware. Was previously locked to bg-navy-500 with the
 * white wordmark variant; that worked in dark mode but in light mode
 * left a hard navy band with a white logo at the bottom of the page,
 * out of step with the rest of the marketing site (which is now
 * surface/page-bg with the dark wordmark). Switched to the same CSS
 * tokens the rest of the site uses (`--color-surface`, `--color-on-
 * surface`, `--color-on-subtle`, `--color-border`) and reused the
 * GolfAuthNavLogo component so the footer logo automatically matches
 * the navbar logo's theme variant.
 */
export default function Footer() {
  return (
    <footer
      className="border-t"
      style={{
        backgroundColor: 'var(--color-surface)',
        color: 'var(--color-on-surface)',
        borderColor: 'var(--color-border)',
      }}
    >
      <div className="container mx-auto px-4 md:px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Logo & description */}
          <div className="col-span-1 md:col-span-1">
            <Link to="/" aria-label="GolfAuth home" className="inline-block max-w-full">
              <GolfAuthNavLogo className="h-8 w-auto max-h-10 max-w-[min(180px,70vw)]" />
            </Link>
            <p className="mt-4 text-sm" style={{ color: 'var(--color-on-subtle)' }}>
              Infrastructure designed for Golf
            </p>
            <div className="flex space-x-4 mt-6">
              {[
                { Icon: Instagram, label: 'GolfAuth on Instagram', href: 'https://www.instagram.com/golfauth/', external: true },
                { Icon: Linkedin, label: 'GolfAuth on LinkedIn', href: 'https://www.linkedin.com/company/golfauth/', external: true },
                { Icon: Facebook, label: 'GolfAuth on Facebook', href: 'https://www.facebook.com/profile.php?id=61560905070567&sk=about', external: true },
                { Icon: Mail, label: 'Email GolfAuth', href: 'mailto:contact@golfauth.com', external: false },
              ].map(({ Icon, label, href, external }) => (
                <a
                  key={label}
                  href={href}
                  aria-label={label}
                  {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                  className="transition-colors hover:opacity-80"
                  style={{ color: 'var(--color-on-subtle)' }}
                >
                  <Icon size={20} aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-on-surface)' }}>
              Quick Links
            </h3>
            <ul className="space-y-3">
              {[
                { label: 'Home', to: '/' },
                ...(IS_NATIVE ? [] : [{ label: 'Find a Professional', to: '/find-professional' }]),
                { label: 'About Us', to: '/about' },
                ...(IS_NATIVE ? [] : [{ label: 'Team', to: '/team' }]),
                ...(IS_NATIVE ? [] : [{ label: 'Contact', to: '/contact' }]),
              ].map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="transition-colors hover:opacity-80"
                    style={{ color: 'var(--color-on-subtle)' }}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal — Privacy, Terms, and Data Retention. Privacy + Terms
              show a small "(draft)" badge until 1 July 2026, when the
              documents become legally effective. After that date the
              badge auto-disappears (date check on render). */}
          <div>
            <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-on-surface)' }}>
              Legal
            </h3>
            <ul className="space-y-3">
              {LEGAL_LINKS.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="transition-colors hover:opacity-80 inline-flex items-baseline gap-2"
                    style={{ color: 'var(--color-on-subtle)' }}
                  >
                    {link.label}
                    {link.draftable && IS_LEGAL_DRAFT_PERIOD && <DraftBadge />}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-on-surface)' }}>
              Stay Updated
            </h3>
            <p className="mb-4 text-sm" style={{ color: 'var(--color-on-subtle)' }}>
              Stay updated on GolfAuth progress
            </p>
            <form className="space-y-2">
              <label htmlFor="footer-newsletter-email" className="sr-only">
                Email address
              </label>
              {/* Input had bg-slate-800/border-slate-700 hardcoded — almost
                  invisible against the new surface bg in light mode. Using
                  page-bg + border tokens so it stays clearly outlined in
                  both light and dark mode. */}
              <input
                id="footer-newsletter-email"
                type="email"
                placeholder="Email Address"
                className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-brand-500"
                style={{
                  backgroundColor: 'var(--color-page-bg)',
                  borderColor: 'var(--color-border)',
                  color: 'var(--color-on-surface)',
                }}
                required
              />
              <button type="submit" className="w-full btn-primary">
                Subscribe
              </button>
            </form>
          </div>
        </div>

        <div
          className="mt-12 pt-8 border-t text-center text-sm"
          style={{
            borderColor: 'var(--color-border)',
            color: 'var(--color-on-subtle)',
          }}
        >
          <p>&copy; {new Date().getFullYear()} GolfAuth. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}